// A ponte entre o miolo do grafo (nucleo.ts, desenho.ts) e o React.
//
// Divisão de trabalho: o que muda 60 vezes por segundo — posição dos nós,
// câmera, quem está sob o cursor — vive num ref, porque virar estado do React
// faria o componente renderizar a cada quadro. O que a interface mostra —
// filtros ligados, nó selecionado, contadores — é estado de verdade.
//
// O laço de animação é o mesmo de graph.js:220: esfria o alpha, dá um passo da
// física e desenha. Ele nunca para enquanto a aba está aberta (o desenho é
// barato e o hover precisa redesenhar), mas a física só roda enquanto há calor.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ajustar, casaComBusca, escolher, estaRecortado, filtrar, graus, isolados, paraMundo,
  rankDeBandas, semear, passo, tiposPadrao, vizinhanca,
  type Aresta, type Camera, type Filtros, type GrafoBruto, type Layout, type Modo, type No,
  type NoBruto,
} from './nucleo';
import { desenhar, type Cor } from './desenho';

/** Onde a dica flutuante aparece, e sobre quem. */
export interface Dica { x: number; y: number; no: No }

interface Vista {
  nos: No[];
  arestas: Aresta[];
  mapa: Map<string, No>;
  rank: Map<number, number>;
  graus: Map<string, number>;
  camera: Camera;
  alpha: number;
  layout: Layout;
  modo: Modo;
  selecionado: No | null;
  sobre: No | null;
  busca: string;
  rotulos: boolean;
  profundidade: number;
  arrastando: No | null;
  pan: { x: number; y: number; vx: number; vy: number } | null;
  largura: number;
  altura: number;
  notas: Record<string, number>;
  ajustado: boolean;
}

/**
 * Resolve as variáveis de tema uma vez por leitura, em vez de por traço.
 *
 * O `cv()` do painel antigo chamava getComputedStyle dentro do laço de desenho;
 * num grafo de 240 nós isso é leitura de estilo em milhares por segundo. O cache
 * é limpo quando o tema troca (o efeito adiante observa o data-theme).
 */
function fabricarCor(): { cor: Cor; limpar: () => void } {
  let cache = new Map<string, string>();
  const cor: Cor = (v) => {
    const achado = cache.get(v);
    if (achado !== undefined) return achado;
    const lido = getComputedStyle(document.documentElement).getPropertyValue(v).trim() || '#888';
    cache.set(v, lido);
    return lido;
  };
  return { cor, limpar: () => { cache = new Map(); } };
}

export interface GrafoVivo {
  /**
   * Refs de callback, e não de objeto: a aba mostra "carregando" antes de o
   * grafo chegar, então o canvas aparece depois da primeira renderização. Com
   * ref de objeto o efeito que mede a moldura rodava no vazio e o tamanho ficava
   * zerado para sempre — canvas em branco com a barra de estado dizendo 91 nós.
   */
  aoMontarCanvas: (el: HTMLCanvasElement | null) => void;
  aoMontarMoldura: (el: HTMLDivElement | null) => void;
  /** a moldura para quem precisa medir na hora, como a dica flutuante */
  refMoldura: React.RefObject<HTMLDivElement | null>;
  filtros: Filtros;
  layout: Layout;
  rotulos: boolean;
  busca: string;
  dica: Dica | null;
  selecionado: No | null;
  zoom: number;
  /** nós e arestas visíveis contra o total, para a barra de estado */
  visiveis: { nos: number; arestas: number; isolados: number };
  recortado: boolean;
  alternarTipo: (k: string) => void;
  alternarRelacao: (r: string) => void;
  setProfundidade: (d: number) => void;
  setEsconderIsolados: (v: boolean) => void;
  setLayout: (l: Layout) => void;
  setRotulos: (v: boolean) => void;
  setBusca: (q: string) => void;
  /** nós visíveis que casam com a busca; só a chave e a contagem são usadas */
  achados: NoBruto[];
  ajustarTela: () => void;
  reorganizar: () => void;
  limparFiltros: () => void;
  focarNo: (chave: string) => void;
  selecionar: (n: No | null) => void;
  isolarVizinhanca: () => void;
  verTudo: () => void;
  eventos: {
    onMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onDoubleClick: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseLeave: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLCanvasElement>) => void;
  };
}

export function useGrafo(
  grafo: GrafoBruto | null,
  modo: Modo,
  notasPorArquivo: Record<string, number>,
  aoLimpar?: (recado: string) => void,
): GrafoVivo {
  const refCanvas = useRef<HTMLCanvasElement | null>(null);
  const refMoldura = useRef<HTMLDivElement | null>(null);
  // conta as montagens do canvas para o efeito de medida rodar quando ele
  // realmente existe, e não uma vez só, antes de o grafo chegar
  const [montado, setMontado] = useState(0);
  const aoMontarCanvas = useCallback((el: HTMLCanvasElement | null) => {
    refCanvas.current = el;
    if (el) setMontado((n) => n + 1);
  }, []);
  const aoMontarMoldura = useCallback((el: HTMLDivElement | null) => {
    refMoldura.current = el;
    if (el) setMontado((n) => n + 1);
  }, []);

  const [filtros, setFiltros] = useState<Filtros>(() => ({
    tipos: tiposPadrao(grafo, modo),
    relacoes: new Set((grafo?.edges || []).map((e) => e.rel)),
    esconderIsolados: false,
    profundidade: 0,
  }));
  const [layout, setLayoutEstado] = useState<Layout>('layers');
  const [rotulos, setRotulos] = useState(true);
  const [busca, setBuscaEstado] = useState('');
  const [dica, setDica] = useState<Dica | null>(null);
  const [selecionado, setSelecionado] = useState<No | null>(null);
  const [zoom, setZoom] = useState(1);
  const [visiveis, setVisiveis] = useState({ nos: 0, arestas: 0, isolados: 0 });
  const [aparecendo, setAparecendo] = useState<Set<string>>(() => new Set());

  const vista = useRef<Vista>({
    nos: [], arestas: [], mapa: new Map(), rank: new Map(), graus: new Map(),
    camera: { x: 0, y: 0, k: 1 }, alpha: 0, layout: 'layers', modo,
    selecionado: null, sobre: null, busca: '', rotulos: true, profundidade: 0,
    arrastando: null, pan: null, largura: 0, altura: 0, notas: notasPorArquivo, ajustado: false,
  });
  const cores = useRef(fabricarCor());

  // espelha no ref o que o laço de desenho precisa ler
  vista.current.layout = layout;
  vista.current.modo = modo;
  vista.current.busca = busca;
  vista.current.rotulos = rotulos;
  vista.current.profundidade = filtros.profundidade;
  vista.current.notas = notasPorArquivo;

  const focoAtual = useCallback((): Set<string> | null => {
    const v = vista.current;
    return v.selecionado && v.profundidade
      ? vizinhanca(v.arestas, v.selecionado.node_key, v.profundidade)
      : null;
  }, []);

  const contar = useCallback(() => {
    const v = vista.current;
    setVisiveis({
      nos: v.nos.length,
      arestas: v.arestas.length,
      isolados: isolados(v.nos, v.arestas).length,
    });
    setAparecendo(new Set(v.nos.map((n) => n.node_key)));
    setZoom(v.camera.k);
  }, []);

  const ajustarTela = useCallback((lista?: No[]) => {
    const v = vista.current;
    const c = ajustar(lista && lista.length ? lista : v.nos, v.largura, v.altura);
    if (!c) return;
    v.camera = c;
    v.ajustado = true;
    setZoom(c.k);
  }, []);

  /** Reconstrói a lista visível a partir do grafo e dos filtros atuais. */
  const reconstruir = useCallback((filtrosAgora: Filtros, resemear: boolean) => {
    const v = vista.current;
    const r = filtrar(grafo, filtrosAgora, v.nos);
    v.nos = r.nos;
    v.arestas = r.arestas;
    v.mapa = r.mapa;
    v.graus = graus(grafo?.edges || []);
    v.rank = rankDeBandas(v.nos);
    if (resemear || v.nos.some((n) => !n.seeded)) semear(v.nos, v.layout, v.rank);
    v.alpha = 1;
    if (v.selecionado && !v.mapa.has(v.selecionado.node_key)) {
      v.selecionado = null;
      setSelecionado(null);
    }
    contar();
    // semeou de novo: enquadra depois de alguns quadros, quando as camadas já
    // assentaram. É o mesmo atraso do painel atual (graph.js:315); enquadrar no
    // mesmo instante pegaria os nós ainda empilhados na origem.
    if (resemear) {
      v.ajustado = false;
      setTimeout(() => ajustarTela(), 60);
    }
  }, [grafo, contar, ajustarTela]);

  /**
   * Grafo novo (troca de projeto ou de modo): filtros voltam ao padrão do modo.
   *
   * Isto roda na renderização, e não num efeito, porque efeito roda depois da
   * pintura: por um quadro a tela mostrava o grafo novo com os filtros do antigo
   * e, quando os tipos não coincidiam, piscava "os filtros escondem tudo" antes
   * de se corrigir. Ajustar estado durante a renderização faz o React descartar
   * esta passada e refazê-la antes de pintar, que é justamente o que se quer.
   *
   * A origem fica em estado, não em ref: o React pode jogar uma renderização
   * fora, e estado volta atrás junto com ela, enquanto ref não — com ref, a
   * comparação já dava "igual" na tentativa seguinte e os filtros ficavam os do
   * grafo vazio, com tudo desligado.
   */
  const [origem, setOrigem] = useState<{ g: GrafoBruto | null; m: Modo } | null>(null);
  if (!origem || origem.g !== grafo || origem.m !== modo) {
    setOrigem({ g: grafo, m: modo });
    const novos: Filtros = {
      tipos: tiposPadrao(grafo, modo),
      relacoes: new Set((grafo?.edges || []).map((e) => e.rel)),
      esconderIsolados: false,
      profundidade: 0,
    };
    vista.current.selecionado = null;
    vista.current.sobre = null;
    setSelecionado(null);
    setDica(null);
    setFiltros(novos);
    reconstruir(novos, true);
  }

  /* ------------------------------------------------------------- tamanho */
  useEffect(() => {
    const moldura = refMoldura.current;
    const tela = refCanvas.current;
    if (!moldura || !tela) return;
    const medir = () => {
      const r = moldura.getBoundingClientRect();
      const d = window.devicePixelRatio || 1;
      const v = vista.current;
      v.largura = r.width;
      v.altura = r.height;
      tela.width = r.width * d;
      tela.height = r.height * d;
      tela.getContext('2d')?.setTransform(d, 0, 0, d, 0, 0);
      // a aba do grafo nasce escondida: o primeiro tamanho de verdade só chega
      // aqui, e é ele que permite o enquadramento inicial
      if (!v.ajustado) {
        const c = ajustar(v.nos, v.largura, v.altura);
        if (c) { v.camera = c; v.ajustado = true; setZoom(c.k); }
      }
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(moldura);
    return () => observador.disconnect();
  }, [montado]);

  /* ------------------------------------------------------------- tema */
  useEffect(() => {
    const observador = new MutationObserver(() => cores.current.limpar());
    observador.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observador.disconnect();
  }, []);

  /* ------------------------------------------------------------- laço */
  useEffect(() => {
    let vivo = true;
    let quadro = 0;
    const passar = () => {
      if (!vivo) return;
      const v = vista.current;
      const ctx = refCanvas.current?.getContext('2d');
      if (v.alpha > 0.008) {
        passo({
          nos: v.nos, arestas: v.arestas, mapa: v.mapa, rank: v.rank,
          layout: v.layout, alpha: v.alpha, arrastando: v.arrastando,
        });
        v.alpha *= 0.988;
      }
      if (ctx) {
        desenhar({
          ctx,
          largura: v.largura,
          altura: v.altura,
          camera: v.camera,
          nos: v.nos,
          arestas: v.arestas,
          mapa: v.mapa,
          rank: v.rank,
          layout: v.layout,
          modo: v.modo,
          selecionado: v.selecionado,
          sobre: v.sobre,
          foco: focoAtual(),
          busca: v.busca,
          rotulos: v.rotulos,
          notasPorArquivo: v.notas,
          cor: cores.current.cor,
          graus: v.graus,
        });
      }
      quadro = requestAnimationFrame(passar);
    };
    quadro = requestAnimationFrame(passar);
    return () => { vivo = false; cancelAnimationFrame(quadro); };
  }, [focoAtual]);

  /* ------------------------------------------------------------- ações */
  const aplicar = useCallback((mudanca: Partial<Filtros>, resemear: boolean) => {
    setFiltros((antes) => {
      const depois = { ...antes, ...mudanca };
      reconstruir(depois, resemear);
      return depois;
    });
  }, [reconstruir]);

  const alternarTipo = useCallback((k: string) => {
    setFiltros((antes) => {
      const tipos = new Set(antes.tipos);
      if (tipos.has(k)) tipos.delete(k); else tipos.add(k);
      const depois = { ...antes, tipos };
      reconstruir(depois, true);
      return depois;
    });
  }, [reconstruir]);

  const alternarRelacao = useCallback((r: string) => {
    setFiltros((antes) => {
      const relacoes = new Set(antes.relacoes);
      if (relacoes.has(r)) relacoes.delete(r); else relacoes.add(r);
      const depois = { ...antes, relacoes };
      reconstruir(depois, false);
      return depois;
    });
  }, [reconstruir]);

  const ajustarNoFoco = useCallback(() => {
    const v = vista.current;
    const f = focoAtual();
    ajustarTela(f ? v.nos.filter((n) => f.has(n.node_key)) : undefined);
  }, [ajustarTela, focoAtual]);

  const selecionar = useCallback((n: No | null) => {
    vista.current.selecionado = n;
    setSelecionado(n);
    if (n) vista.current.alpha = Math.max(vista.current.alpha, 0.1);
    contar();
  }, [contar]);

  const focarNo = useCallback((chave: string) => {
    const v = vista.current;
    let n = v.mapa.get(chave);
    if (!n) {
      // o nó está filtrado: religa o tipo dele e reconstrói
      const bruto = (grafo?.nodes || []).find((x) => x.node_key === chave);
      if (!bruto) return;
      setFiltros((antes) => {
        const depois = { ...antes, tipos: new Set(antes.tipos).add(bruto.kind) };
        reconstruir(depois, false);
        return depois;
      });
      n = vista.current.mapa.get(chave);
      if (!n) return;
    }
    selecionar(n);
    v.camera = { x: n.x, y: n.y, k: Math.max(v.camera.k, 0.9) };
    v.alpha = Math.max(v.alpha, 0.12);
    setZoom(v.camera.k);
  }, [grafo, reconstruir, selecionar]);

  const limparFiltros = useCallback(() => {
    const novos: Filtros = {
      tipos: tiposPadrao(grafo, modo),
      relacoes: new Set((grafo?.edges || []).map((e) => e.rel)),
      esconderIsolados: false,
      profundidade: 0,
    };
    setFiltros(novos);
    setBuscaEstado('');
    reconstruir(novos, true);
    // o enquadramento faz parte da saída do estado vazio: os filtros voltaram,
    // mas a câmera continuaria onde o recorte a tinha deixado
    setTimeout(() => ajustarTela(), 0);
    aoLimpar?.('filtros limpos — mostrando o grafo inteiro');
  }, [grafo, modo, reconstruir, ajustarTela, aoLimpar]);

  const reorganizar = useCallback(() => {
    const v = vista.current;
    for (const n of v.nos) n.pinned = false;
    semear(v.nos, v.layout, v.rank);
    v.alpha = 1;
    setTimeout(() => ajustarTela(), 80);
  }, [ajustarTela]);

  const setLayout = useCallback((l: Layout) => {
    setLayoutEstado(l);
    const v = vista.current;
    v.layout = l;
    semear(v.nos, l, v.rank);
    v.alpha = 1;
    setTimeout(() => ajustarTela(), 80);
  }, [ajustarTela]);

  const setProfundidade = useCallback((d: number) => {
    vista.current.profundidade = d;
    aplicar({ profundidade: d }, false);
    if (d) setTimeout(() => ajustarNoFoco(), 0);
    else setTimeout(() => ajustarTela(), 0);
  }, [aplicar, ajustarNoFoco, ajustarTela]);

  const isolarVizinhanca = useCallback(() => {
    setProfundidade(vista.current.profundidade || 1);
  }, [setProfundidade]);

  const verTudo = useCallback(() => setProfundidade(0), [setProfundidade]);

  const setBusca = useCallback((q: string) => {
    setBuscaEstado(q);
    vista.current.busca = q;
  }, []);

  /* ------------------------------------------------------------- eventos */
  const ponto = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const v = vista.current;
    const p = ponto(e);
    if (v.arrastando) {
      const m = paraMundo(p, v.camera, v.largura, v.altura);
      v.arrastando.x = m.x;
      v.arrastando.y = m.y;
      v.arrastando.vx = 0;
      v.arrastando.vy = 0;
      v.arrastando.pinned = true;
    } else if (v.pan) {
      v.camera = {
        ...v.camera,
        x: v.pan.vx - (p.x - v.pan.x) / v.camera.k,
        y: v.pan.vy - (p.y - v.pan.y) / v.camera.k,
      };
    } else {
      v.sobre = escolher(v.nos, p, v.camera, v.largura, v.altura);
    }
    // a dica só existe para quem está fora do painel de detalhe: repetir ali o
    // que o detalhe já mostra é ruído sobre o desenho
    const alvo = v.sobre && v.sobre !== v.selecionado ? v.sobre : null;
    setDica((antes) => {
      if (!alvo) return antes ? null : antes;
      return { x: p.x, y: p.y, no: alvo };
    });
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const v = vista.current;
    const p = ponto(e);
    const n = escolher(v.nos, p, v.camera, v.largura, v.altura);
    if (n) {
      v.arrastando = n;
      selecionar(n);
      setDica(null);
    } else {
      v.pan = { x: p.x, y: p.y, vx: v.camera.x, vy: v.camera.y };
      // a mão fechada durante o arrasto do fundo vem do CSS do painel atual
      refCanvas.current?.classList.add('dragging');
    }
  }, [selecionar]);

  // solta no window, e não no canvas: o botão costuma ser liberado fora dele
  useEffect(() => {
    const soltar = () => {
      vista.current.arrastando = null;
      if (vista.current.pan) {
        vista.current.pan = null;
        setZoom(vista.current.camera.k);
      }
      refCanvas.current?.classList.remove('dragging');
    };
    window.addEventListener('mouseup', soltar);
    return () => window.removeEventListener('mouseup', soltar);
  }, []);

  const onDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const v = vista.current;
    const n = escolher(v.nos, ponto(e), v.camera, v.largura, v.altura);
    if (!n) return;
    n.pinned = false;
    v.alpha = Math.max(v.alpha, 0.3);
  }, []);

  // a roda entra por addEventListener com passive:false; no React o onWheel do
  // JSX é registrado como passivo e o preventDefault seria ignorado — a página
  // rolaria junto com o zoom
  useEffect(() => {
    const tela = refCanvas.current;
    if (!tela) return;
    const naRoda = (e: WheelEvent) => {
      e.preventDefault();
      const v = vista.current;
      const r = tela.getBoundingClientRect();
      const p = { x: e.clientX - r.left, y: e.clientY - r.top };
      const antes = paraMundo(p, v.camera, v.largura, v.altura);
      const k = Math.max(0.1, Math.min(3.2, v.camera.k * (e.deltaY < 0 ? 1.12 : 0.89)));
      const camera = { ...v.camera, k };
      const depois = paraMundo(p, camera, v.largura, v.altura);
      v.camera = { k, x: camera.x + antes.x - depois.x, y: camera.y + antes.y - depois.y };
      setZoom(k);
    };
    tela.addEventListener('wheel', naRoda, { passive: false });
    return () => tela.removeEventListener('wheel', naRoda);
    // montado entra na lista porque o canvas só existe depois que o grafo
    // carrega: sem isto o ouvinte da roda era registrado num ref ainda vazio e
    // o zoom pela roda simplesmente não acontecia
  }, [montado]);

  const onMouseLeave = useCallback(() => {
    vista.current.sobre = null;
    setDica(null);
  }, []);

  // a busca só acha o que está na tela, e a lista de nós vive num ref; o
  // conjunto de chaves visíveis é o espelho dela em estado, para o contador de
  // achados não congelar depois de um filtro
  const achados = useMemo(
    () => (busca
      ? (grafo?.nodes || []).filter((n) => aparecendo.has(n.node_key) && casaComBusca(n, busca))
      : []),
    [busca, grafo, aparecendo],
  );

  // canvas não se navega com Tab; com o foco nele, setas arrastam e +/- aproximam.
  // a seta anda o mesmo tanto em qualquer zoom, como no painel atual: quem está
  // aproximado quer ajuste fino, não um salto proporcional
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLCanvasElement>) => {
    const v = vista.current;
    const mover = (dx: number, dy: number) => {
      e.preventDefault();
      v.camera = { ...v.camera, x: v.camera.x + dx, y: v.camera.y + dy };
    };
    // a seta arrasta o conteúdo: seta para a esquerda leva a câmera para a direita
    if (e.key === 'ArrowLeft') mover(60, 0);
    if (e.key === 'ArrowRight') mover(-60, 0);
    if (e.key === 'ArrowUp') mover(0, 60);
    if (e.key === 'ArrowDown') mover(0, -60);
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      v.camera = { ...v.camera, k: Math.min(3.2, v.camera.k * 1.2) };
      setZoom(v.camera.k);
    }
    if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      v.camera = { ...v.camera, k: Math.max(0.15, v.camera.k / 1.2) };
      setZoom(v.camera.k);
    }
  }, []);

  const recortado = useMemo(
    () => estaRecortado(grafo, filtros, modo),
    [grafo, filtros, modo],
  );

  // identidade estável: o atalho de teclado da tela depende destas funções, e
  // recriá-las por render trocaria o ouvinte do documento 60 vezes por segundo
  const ajustarTudo = useCallback(() => ajustarTela(), [ajustarTela]);
  const esconderIsolados = useCallback(
    (v: boolean) => aplicar({ esconderIsolados: v }, false),
    [aplicar],
  );

  return {
    aoMontarCanvas,
    aoMontarMoldura,
    refMoldura,
    filtros,
    layout,
    rotulos,
    busca,
    dica,
    selecionado,
    zoom,
    visiveis,
    recortado,
    alternarTipo,
    alternarRelacao,
    setProfundidade,
    setEsconderIsolados: esconderIsolados,
    setLayout,
    setRotulos,
    setBusca,
    achados,
    ajustarTela: ajustarTudo,
    reorganizar,
    limparFiltros,
    focarNo,
    selecionar,
    isolarVizinhanca,
    verTudo,
    eventos: { onMouseMove, onMouseDown, onDoubleClick, onMouseLeave, onKeyDown },
  };
}
